/**
 * Value-over-time plot of a curve (W2-05; DESIGN.md §4.12 "value-over-time plot", §5.7): the segment's value from
 * its start value to its end value with the candidate curve (Phira's formulas: `Ease.modelY`, Stay as a step at the
 * end), the current curve dashed for comparison, the area beyond the start / end values shaded, and a dot following
 * the playhead while the live preview loops (no dot under reduced motion).
 */

import { useEffect, useId, useMemo, useState } from 'react';
import { Ease } from '../../core/ease/index';
import type { Ease as EaseValue } from '../../core/ease/types';
import { prefersReducedMotion } from '../../ui/internal/useReducedMotion';
import s from './Easing.module.css';
import type { ProgressSource } from './livePreview';

const SAMPLES = 96;

export interface CurvePreviewProps {
  readonly ease: EaseValue;
  /** Curve before the change, drawn dashed (null: none). */
  readonly compare?: EaseValue | null;
  /** Start and end values (numbers); omitted: the plot is normalised 0..1. */
  readonly from?: number;
  readonly to?: number;
  readonly formatValue?: (v: number) => string;
  /** Accessible description of the plot. */
  readonly label: string;
  readonly width?: number;
  readonly height?: number;
  /**
   * Progress source for the moving dot: polled every frame while it returns numbers; when it returns null the
   * polling stops until the source wakes it (looping started again).
   */
  readonly progress?: ProgressSource;
}

interface Sampled {
  readonly points: ReadonlyArray<readonly [number, number]>;
  readonly lo: number;
  readonly hi: number;
}

function sample(e: EaseValue): Sampled | null {
  if (e.kind === 'hold') {
    return {
      points: [
        [0, 0],
        [1, 0],
        [1, 1],
      ],
      lo: 0,
      hi: 1,
    };
  }
  const points: Array<readonly [number, number]> = [];
  let lo = 0;
  let hi = 1;
  for (let i = 0; i <= SAMPLES; i++) {
    const x = i / SAMPLES;
    const y = Ease.modelY(e, x);
    if (!Number.isFinite(y)) return null;
    lo = Math.min(lo, y);
    hi = Math.max(hi, y);
    points.push([x, y]);
  }
  return { points, lo, hi };
}

function yAt(points: ReadonlyArray<readonly [number, number]>, x: number): number {
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    if (a === undefined || b === undefined) break;
    if (x <= b[0]) {
      const span = b[0] - a[0];
      return span <= 0 ? b[1] : a[1] + ((x - a[0]) / span) * (b[1] - a[1]);
    }
  }
  return points[points.length - 1]?.[1] ?? 0;
}

function useProgress(source: ProgressSource | undefined): number | null {
  const [p, setP] = useState<number | null>(null);
  useEffect(() => {
    if (source === undefined || prefersReducedMotion() || typeof requestAnimationFrame !== 'function') return undefined;
    let raf = 0;
    const tick = (): void => {
      raf = 0;
      const v = source.progress();
      setP(v);
      // Nothing loops: stop polling until the source says looping started again.
      if (v !== null) raf = requestAnimationFrame(tick);
    };
    const wake = (): void => {
      if (raf === 0) raf = requestAnimationFrame(tick);
    };
    const unsubscribe = source.subscribe(wake);
    wake();
    return () => {
      unsubscribe();
      if (raf !== 0) cancelAnimationFrame(raf);
    };
  }, [source]);
  return p;
}

export function CurvePreview(props: CurvePreviewProps) {
  const width = props.width ?? 220;
  const height = props.height ?? 120;
  const titleId = useId();
  const main = useMemo(() => sample(props.ease), [props.ease]);
  const compare = useMemo(
    () => (props.compare === undefined || props.compare === null ? null : sample(props.compare)),
    [props.compare],
  );
  const progress = useProgress(props.progress);
  const pad = { l: 8, r: 8, t: 14, b: 14 };
  const hasValues = props.from !== undefined && props.to !== undefined;
  // A falling value is drawn falling: y runs from the start value to the end value in value space.
  const flip = hasValues && (props.to ?? 0) < (props.from ?? 0);
  const u = (y: number): number => (flip ? 1 - y : y);
  const rawLo = Math.min(main?.lo ?? 0, compare?.lo ?? 0);
  const rawHi = Math.max(main?.hi ?? 1, compare?.hi ?? 1);
  const lo = Math.min(u(rawLo), u(rawHi));
  const hi = Math.max(u(rawLo), u(rawHi));
  const px = (x: number): number => pad.l + x * (width - pad.l - pad.r);
  const py = (v: number): number => pad.t + ((hi - v) / (hi - lo)) * (height - pad.t - pad.b);
  const line = (smp: Sampled | null): string =>
    smp === null ? '' : smp.points.map(([x, y]) => `${px(x).toFixed(2)},${py(u(y)).toFixed(2)}`).join(' ');
  const fmt = props.formatValue ?? ((v: number) => String(Math.round(v * 100) / 100));
  const dot = progress !== null && main !== null ? [px(progress), py(u(yAt(main.points, progress)))] : null;
  const top = Math.max(u(0), u(1));
  const bottom = Math.min(u(0), u(1));
  return (
    <svg
      className={s.plot}
      width={width}
      height={height}
      viewBox={`0 0 ${String(width)} ${String(height)}`}
      role="img"
      aria-labelledby={titleId}
    >
      <title id={titleId}>{props.label}</title>
      {hi > top && (
        <rect
          className={s.plotOvershoot}
          x={pad.l}
          y={py(hi)}
          width={width - pad.l - pad.r}
          height={py(top) - py(hi)}
        />
      )}
      {lo < bottom && (
        <rect
          className={s.plotOvershoot}
          x={pad.l}
          y={py(bottom)}
          width={width - pad.l - pad.r}
          height={py(lo) - py(bottom)}
        />
      )}
      <line className={s.plotGuide} x1={pad.l} x2={width - pad.r} y1={py(0)} y2={py(0)} />
      <line className={s.plotGuide} x1={pad.l} x2={width - pad.r} y1={py(1)} y2={py(1)} />
      {compare !== null && <polyline className={s.plotCompare} points={line(compare)} />}
      {main !== null && <polyline className={s.plotCurve} points={line(main)} />}
      {dot !== null && <circle className={s.plotDot} cx={dot[0]} cy={dot[1]} r={3.5} />}
      {hasValues && (
        <>
          <text className={s.plotValue} x={pad.l} y={flip ? py(u(0)) - 4 : py(u(0)) + 11}>
            {fmt(props.from ?? 0)}
          </text>
          <text className={s.plotValue} x={width - pad.r} y={flip ? py(u(1)) + 11 : py(u(1)) - 4} textAnchor="end">
            {fmt(props.to ?? 1)}
          </text>
        </>
      )}
    </svg>
  );
}
